import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// Placeholder images for bedroom inspiration
const bedroomImages = {
  hero: "https://images.unsplash.com/photo-1540518614846-7eded433c457?auto=format&fit=crop&w=1200&q=80",
  roman: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=600&q=80",
  blackout: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=600&q=80",
};

function BedroomBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-gray-900">
      <Helmet>
        <title>Bedroom Blinds UK | Blackout & Thermal Blinds for Better Sleep</title>
        <meta
          name="description"
          content="Transform your sleep with 100% blackout bedroom blinds. Explore thermal Roman blinds, stylish rollers, and motorized options at MF Blinds UK."
        />
        <meta name="keywords" content="Bedroom Blinds UK, Blackout Blinds, Roman Blinds Bedroom, Thermal Bedroom Blinds, Nursery Blinds, Sleep Hygiene Window Coverings" />
      </Helmet>

      {/* --- Hero Section: Focus on Sleep Quality --- */}
      <header className="grid lg:grid-cols-2 gap-12 items-center mb-20">
        <div>
          <span className="text-indigo-600 font-bold tracking-widest uppercase text-sm">Sleep Better, Live Better</span>
          <h1 className="text-4xl md:text-6xl font-extrabold mt-2 mb-6 leading-tight">
            Bedroom <span className="text-indigo-600">Blinds</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Your bedroom is your sanctuary. Our <strong>made-to-measure bedroom blinds</strong> combine high-end aesthetics with <strong>total light control</strong>, helping you achieve the perfect environment for a restful night's sleep.
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="bg-indigo-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-indigo-700 transition shadow-lg">
              Explore Blackout Range
            </button>
            <Link to="/measuring-guide" className="border-2 border-gray-300 px-8 py-4 rounded-lg font-bold hover:bg-gray-50 transition">
              Free Measuring Guide
            </Link>
          </div>
        </div>
        <div className="relative">
          <img 
            src={bedroomImages.hero} 
            alt="Cozy modern bedroom with soft grey blackout blinds" 
            className="rounded-2xl shadow-2xl h-[400px] w-full object-cover"
          />
        </div>
      </header>

      {/* --- Viral Keywords / Benefits Grid --- */}
      <section className="py-12 border-y border-gray-100 mb-20">
        <div className="grid md:grid-cols-3 gap-10">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center text-3xl mb-4">🌙</div>
            <h3 className="text-lg font-bold">100% Blackout</h3>
            <p className="text-gray-600 text-sm">Eliminate streetlights and early morning sun for uninterrupted REM sleep.</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center text-3xl mb-4">🌡️</div>
            <h3 className="text-lg font-bold">Thermal Regulation</h3>
            <p className="text-gray-600 text-sm">Keep your bedroom warm in winter and cool in summer with honeycomb tech.</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center text-3xl mb-4">🤫</div>
            <h3 className="text-lg font-bold">Noise Reduction</h3>
            <p className="text-gray-600 text-sm">Thick, high-quality fabrics help dampen external traffic and neighborhood noise.</p>
          </div>
        </div>
      </section>

      {/* --- Style Selection --- */}
      <h2 className="text-3xl font-bold mb-10 text-center">Find Your Bedroom Style</h2>
      <div className="grid md:grid-cols-2 gap-12 mb-20">
        <div className="group cursor-pointer">
          <div className="overflow-hidden rounded-xl mb-4">
            <img src={bedroomImages.roman} alt="Elegant Roman blinds" className="group-hover:scale-105 transition duration-500" />
          </div>
          <h3 className="text-2xl font-bold mb-2">Luxury Roman Blinds</h3>
          <p className="text-gray-600 mb-4">For a soft, opulent look. Our Roman blinds come with an optional blackout lining, combining the beauty of curtains with the functionality of blinds.</p>
          <Link to="/roman-blinds" className="text-indigo-600 font-bold">View Roman Collection →</Link>
        </div>
        <div className="group cursor-pointer">
          <div className="overflow-hidden rounded-xl mb-4">
            <img src={bedroomImages.blackout} alt="Sleek blackout roller blinds" className="group-hover:scale-105 transition duration-500" />
          </div>
          <h3 className="text-2xl font-bold mb-2">Modern Roller Blinds</h3>
          <p className="text-gray-600 mb-4">Minimalist and efficient. These are perfect for smaller bedrooms or nurseries where space-saving and ease-of-use are priorities.</p>
          <Link to="/roller-blinds" className="text-indigo-600 font-bold">View Roller Collection →</Link>
        </div>
      </div>

      {/* --- SEO Deep Dive Section --- */}
      <article className="bg-gray-50 p-10 rounded-3xl mb-20">
        <h2 className="text-3xl font-bold mb-6">Why Blackout Blinds are Essential for Your Health</h2>
        <div className="prose lg:prose-lg max-w-none text-gray-700">
          <p>
            In 2026, sleep experts emphasize that even the smallest amount of ambient light can disrupt Melatonin production. Our <strong>Blackout Bedroom Blinds</strong> utilize multi-layer fabric technology to create a complete seal against external light sources.
          </p>
          <p className="mt-4">
            Whether you are a night shift worker, a parent with a young toddler, or simply someone who values a deep sleep, our made-to-measure solutions ensure that your room stays pitch black until you're ready to wake up. Pair them with <Link to="/perfect-fit-blinds" className="text-indigo-600 underline">Perfect Fit frames</Link> for the ultimate light-blocking experience with no side gaps.
          </p>
        </div>
      </article>

      {/* --- FAQ Section --- */}
      <section className="mb-20">
        <h2 className="text-3xl font-bold mb-8">Bedroom Blinds FAQ</h2>
        <div className="space-y-6">
          <details className="group border-b pb-4">
            <summary className="list-none font-bold text-lg cursor-pointer flex justify-between items-center">
              Are Roman blinds better than roller blinds for bedrooms?
              <span className="group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-3 text-gray-600">Roman blinds offer a more decorative, "dressed" look, while roller blinds are better for a clean, modern aesthetic. Both can be fitted with blackout linings.</p>
          </details>
          <details className="group border-b pb-4">
            <summary className="list-none font-bold text-lg cursor-pointer flex justify-between items-center">
              Do blackout blinds help with heat?
              <span className="group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-3 text-gray-600">Yes! Our thermal-backed blackout blinds reflect heat during the summer and trap warmth during the winter, making your bedroom more energy-efficient.</p>
          </details>
        </div>
      </section>

      {/* --- Cross-Selling / Internal Links --- */}
      <footer className="text-center bg-indigo-900 text-white p-12 rounded-3xl">
        <h2 className="text-3xl font-bold mb-4">Complete Your Home</h2>
        <p className="mb-8 opacity-80">Explore our specialized solutions for every room.</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/bathroom-blinds" className="bg-white text-indigo-900 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition">Bathroom</Link>
          <Link to="/kitchen-blinds" className="bg-white text-indigo-900 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition">Kitchen</Link>
          <Link to="/living-room-blinds" className="bg-white text-indigo-900 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition">Living Room</Link>
        </div>
      </footer>
    </div>
  );
}

export default BedroomBlinds;